export type EditFieldSaveMode = 'AUTO' | 'MANUAL'

/**
 * Single source of truth for per-field save behavior on checklist items.
 *
 * AUTO   = field writes fire immediately on change/blur; no draft buffer;
 *          user does not see a Save button for changes to this field alone.
 * MANUAL = field writes are buffered in the drawer/page draft state;
 *          user must click Save to persist; a Save button appears when
 *          any MANUAL field is dirty.
 *
 * Every field on ChecklistItem/ChecklistDefinition that can be edited via
 * any UI (web drawer, mobile detail page, helper modal, canvas inspector)
 * MUST appear here. The dev-mode assertion in isAutoSaveField/isManualSaveField
 * throws on unknown names so drift is caught early.
 */
export const CHECKLIST_FIELD_SAVE_MODE: Readonly<Record<string, EditFieldSaveMode>> = Object.freeze({
  // --- AUTO fields (fire immediately on change / blur / upload / action; no draft buffer) ---

  // workflow status menu pick or lane drop (onStatusChange / updateChecklistWorkflowStatus)
  workflowStatus: 'AUTO',
  // board column lane drag-and-drop
  boardStatus: 'AUTO',
  // restored status on reopen / rejection
  previousWorkflowStatus: 'AUTO',
  // completion toggle on task status change
  isCompleted: 'AUTO',
  // assignee dropdown menu pick
  assigneeId: 'AUTO',
  // assignee dropdown menu pick (mirrored display name)
  assigneeName: 'AUTO',
  // budget limit input blur (handleBudgetLimitBlur / setBudgetLimitForEntity)
  budgetLimit: 'AUTO',
  // estimated cost input blur in drawer (handleEstimatedCostBlur)
  estimatedCost: 'AUTO',
  // proof upload complete (buildProofSubmissionEffects)
  proofFileUrl: 'AUTO',
  // proof upload complete (buildProofSubmissionEffects)
  proofFileName: 'AUTO',
  // proof upload complete (buildProofSubmissionEffects)
  proofFileStoragePath: 'AUTO',
  // proof upload complete (buildProofSubmissionEffects)
  proofSubmissionId: 'AUTO',
  // proof upload complete (buildProofSubmissionEffects)
  proofUploadedById: 'AUTO',
  // proof upload complete (buildProofSubmissionEffects)
  proofUploadedByName: 'AUTO',
  // proof upload complete (buildProofSubmissionEffects)
  proofUploadedAt: 'AUTO',
  // proof upload or approver sign-off/rejection
  proofReviewStatus: 'AUTO',
  // approver sign-off/rejection timestamp
  proofReviewedAt: 'AUTO',
  // approver user id
  proofReviewedById: 'AUTO',
  // approver user name
  proofReviewedByName: 'AUTO',
  // rejection note submitted by approver
  proofRejectionNote: 'AUTO',
  // set immediately on proof upload when approver is configured
  isPendingReview: 'AUTO',
  // video memo upload complete (persistMemo)
  proofVideoUrl: 'AUTO',
  // audio voice memo upload complete (persistMemo)
  voiceMemoBlobUrl: 'AUTO',
  // memo file storage path
  memoStoragePath: 'AUTO',
  // memo file name
  memoFileName: 'AUTO',
  // memo MIME type
  memoContentType: 'AUTO',
  // memo uploader user id
  memoUploadedById: 'AUTO',
  // memo uploader user name
  memoUploadedByName: 'AUTO',
  // memo upload timestamp
  memoUploadedAt: 'AUTO',
  // card background image upload/clear (persistCardBackground / clearCardBackground)
  cardBackgroundFileId: 'AUTO',
  // card background image download url
  cardBackgroundUrl: 'AUTO',
  // card background image file name
  cardBackgroundFileName: 'AUTO',
  // card background storage path
  cardBackgroundStoragePath: 'AUTO',
  // GPS target location capture button click (captureTargetLocation)
  targetCoordinates: 'AUTO',
  // GPS location check-in verification on completion
  verifiedCoordinates: 'AUTO',
  // atomic increment on comment submission
  commentsCount: 'AUTO',
  // reparent confirmation in location breadcrumb
  parentContainerId: 'AUTO',
  // soft-archive / restore action
  isArchived: 'AUTO',
  // recurring / individual type conversion modal
  checklistType: 'AUTO',
  // inventory scanner scan-out readiness
  isReady: 'AUTO',
  // form attachment / detachment in attachments panel
  attachedFormIds: 'AUTO',
  // design attachment / detachment in attachments panel
  attachedDesignIds: 'AUTO',


  // --- MANUAL fields (buffered in drawer / mobile / inspector draft until explicit Save) ---

  // buffered in draft.title (drawer title commit / mobile draft)
  title: 'MANUAL',
  // buffered in draft.description (drawer, mobile)
  description: 'MANUAL',
  // buffered in draft.completionCriteria (drawer completion criteria textarea, mobile)
  completionCriteria: 'MANUAL',
  // buffered in draft.weightage (drawer dial, mobile draft)
  weightage: 'MANUAL',
  // buffered in draft.priority (mobile draft)
  priority: 'MANUAL',
  // buffered in draft.complexityStars (drawer rating select, mobile draft)
  complexityStars: 'MANUAL',
  // buffered in draft.urgencyStars (drawer rating select, mobile draft)
  urgencyStars: 'MANUAL',
  // buffered in draft.importanceStars (drawer rating select, mobile draft)
  importanceStars: 'MANUAL',
  // buffered in draft.estimatedHours (drawer dial, mobile draft)
  estimatedHours: 'MANUAL',
  // buffered in draft.actualCost (drawer dial, mobile draft)
  actualCost: 'MANUAL',
  // buffered in draft.currencyCode (drawer currency menu, mobile draft)
  currencyCode: 'MANUAL',
  // buffered in draft.scheduleMode (drawer radio, mobile draft)
  scheduleMode: 'MANUAL',
  // buffered in draft.scheduleStartDate (drawer datepicker, mobile draft)
  scheduleStartDate: 'MANUAL',
  // buffered in draft.scheduleEndDate (drawer datepicker, mobile draft)
  scheduleEndDate: 'MANUAL',
  // buffered in draft.startDate (mobile legacy mirror)
  startDate: 'MANUAL',
  // buffered in draft.endDate (mobile legacy mirror)
  endDate: 'MANUAL',
  // buffered in draft.hasTimeWindow (drawer toggle, mobile draft)
  hasTimeWindow: 'MANUAL',
  // buffered in draft.scheduleTimeFrom (drawer time input, mobile draft)
  scheduleTimeFrom: 'MANUAL',
  // buffered in draft.scheduleTimeTo (drawer time input, mobile draft)
  scheduleTimeTo: 'MANUAL',
  // buffered in draft.countdownHours (mobile draft)
  countdownHours: 'MANUAL',
  // buffered in draft.delayDurationHours (mobile draft)
  delayDurationHours: 'MANUAL',
  // buffered in draft.enforcePaintSpringDelay (definition / version patch)
  enforcePaintSpringDelay: 'MANUAL',
  // buffered in draft.requiresLocationPin (drawer setup toggle)
  requiresLocationPin: 'MANUAL',
  // buffered in draft.requiresProofFile (drawer setup toggle)
  requiresProofFile: 'MANUAL',
  // buffered in draft.proofApproverId (drawer setup picker)
  proofApproverId: 'MANUAL',
  // buffered in draft.proofApproverName (drawer setup picker)
  proofApproverName: 'MANUAL',
  // buffered in draft.requiresCoSign (definition / version patch)
  requiresCoSign: 'MANUAL',
  // buffered in draft.coSignerRoleTarget (definition / version patch)
  coSignerRoleTarget: 'MANUAL',
  // buffered in draft / explicit save (dependencies)
  prerequisites: 'MANUAL',
  // buffered in draft / explicit save (dependencies)
  dependencyNodeIds: 'MANUAL',
  // buffered in draft.requiredStockIds (drawer stock tab in recurring/draft mode)
  requiredStockIds: 'MANUAL',
  // buffered in draft.stockRequirements (drawer stock tab in recurring/draft mode)
  stockRequirements: 'MANUAL',
  // buffered in draft.consumptionMode (drawer stock tab in recurring/draft mode)
  consumptionMode: 'MANUAL',
  // buffered in draft.autoSpawnTemplates (inspector / editor)
  autoSpawnTemplates: 'MANUAL',
  // buffered in draft.hasAutoSpawnTrigger (inspector / editor)
  hasAutoSpawnTrigger: 'MANUAL',
  // buffered in draft.isDescriptionPrivate (drawer / editor)
  isDescriptionPrivate: 'MANUAL',
  // buffered in draft.helperVisibilityMask (version patch)
  helperVisibilityMask: 'MANUAL',
  // buffered in draft.isHelperVisible (version patch)
  isHelperVisible: 'MANUAL',

})

export const isAutoSaveField = (name: string): boolean => {
  const mode = CHECKLIST_FIELD_SAVE_MODE[name]
  if (import.meta.env.DEV && mode === undefined) {
    throw new Error(
      `[checklistEditFieldPolicy] Unknown field "${name}". ` +
      `Add it to CHECKLIST_FIELD_SAVE_MODE (AUTO or MANUAL). ` +
      `See plan-checklist-edit-lock-ux.md §2 and §15.`
    )
  }
  return mode === 'AUTO'
}

export const isManualSaveField = (name: string): boolean => {
  const mode = CHECKLIST_FIELD_SAVE_MODE[name]
  if (import.meta.env.DEV && mode === undefined) {
    throw new Error(
      `[checklistEditFieldPolicy] Unknown field "${name}". ` +
      `Add it to CHECKLIST_FIELD_SAVE_MODE (AUTO or MANUAL). ` +
      `See plan-checklist-edit-lock-ux.md §2 and §15.`
    )
  }
  return mode === 'MANUAL'
}

/** All fields grouped by mode. Handy for tests and dev diagnostics. */
export const getFieldsByMode = (mode: EditFieldSaveMode): string[] =>
  Object.entries(CHECKLIST_FIELD_SAVE_MODE)
    .filter(([, m]) => m === mode)
    .map(([name]) => name)
